/*
  TYOHAAR occasion chips — the homepage's occasion circles as the collection
  page's "Shop by" row.

  The categories live in ONE place: the occasion rail section on the homepage.
  Nothing is copied into the collection page's settings.

  HOW IT STAYS FAST
    Liquid on a collection page cannot read a homepage section's blocks, so
    the list travels through the browser:

    1. The homepage rail publishes its tiles (picture, name, link) to
       localStorage as it loads — <tyoh-occ-publish>. Most visits start on the
       homepage, so the list is usually already there.
    2. The collection page draws the chips from that copy the moment the row
       is parsed — this file is loaded without defer for exactly that — so
       they are in the first paint with "All".
    3. It then asks Shopify for the homepage section as it is now (Section
       Rendering API) and redraws only if something changed, so an edit on the
       homepage shows on the next load.

    With no copy yet (a first visit straight to a collection), the row stays
    hidden until the list arrives, so "All" never shows on its own. A CSS
    fallback reveals it after a few seconds whatever happens.

  A section in a JSON template is addressed by an id that carries the
  template's own number (template--123__occasion-rail), and that number differs
  between theme copies. The homepage publishes it alongside the tiles; failing
  that it is read off the homepage itself.
*/

(() => {
  if (customElements.get('tyoh-occ-chips')) return;

  const idKey = (source) => `tyoh-occ-chips:${source}`;
  const dataKey = (source) => `tyoh-occ-chips-data:${source}`;

  const store = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* private mode: the list is fetched every time */
    }
  };

  const recall = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  /*
    Every tile the rail holds, hidden ones included — the View all circle is a
    control, not a category, so it is the only one left out.
  */
  const readTiles = (root) =>
    Array.from(root.querySelectorAll('.occ__item:not(.occ__item--panel) .occ__link'))
      .map((tile) => {
        const image = tile.querySelector('.occ__image');
        return {
          href: tile.getAttribute('href') || '#',
          label: (tile.querySelector('.occ__label')?.textContent || '').trim(),
          src: image ? image.getAttribute('src') : '',
          srcset: image ? image.getAttribute('srcset') || '' : '',
        };
      })
      .filter((tile) => tile.label);

  // A tile with no link renders href="#", which resolves to this very page —
  // without the guard every unlinked chip was marked as the current one.
  const samePage = (href) => {
    if (!href || href.startsWith('#')) return false;
    try {
      return new URL(href, window.location.href).pathname === window.location.pathname;
    } catch (e) {
      return false;
    }
  };

  /* ------------------------------------------------ homepage: publish */

  class TyohOccPublish extends HTMLElement {
    connectedCallback() {
      const rail = this.closest('.occ');
      const source = this.dataset.source;
      if (!rail || !source) return;
      store(dataKey(source), JSON.stringify(readTiles(rail)));
      if (this.dataset.sectionId) store(idKey(source), this.dataset.sectionId);
    }
  }

  /* ------------------------------------------ collection page: draw */

  class TyohOccChips extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.list = document.getElementById(this.dataset.list);
      if (!this.list) return;

      // Fallback reveal for when nothing ever arrives.
      this.revealTimer = setTimeout(() => this.reveal(), 2500);

      const cached = recall(dataKey(this.source));
      if (cached) this.draw(cached);
      this.refresh(cached);
    }

    disconnectedCallback() {
      clearTimeout(this.revealTimer);
    }

    get source() {
      return (this.dataset.source || 'occasion-rail').trim();
    }

    get root() {
      return this.dataset.root || '/';
    }

    reveal() {
      clearTimeout(this.revealTimer);
      this.list.removeAttribute('data-occ-pending');
    }

    draw(json) {
      let tiles;
      try {
        tiles = JSON.parse(json);
      } catch (e) {
        return;
      }
      if (!Array.isArray(tiles)) return;

      this.list.querySelectorAll('[data-occ-chip]').forEach((node) => node.remove());
      const size = this.dataset.size || '84';

      tiles.forEach((tile) => {
        const item = document.createElement('li');
        item.setAttribute('data-occ-chip', '');

        const link = document.createElement('a');
        link.className = 'clh__chip';
        link.href = tile.href;
        if (samePage(tile.href)) link.setAttribute('aria-current', 'true');

        const media = document.createElement('span');
        media.className = 'clh__chip-media';
        media.setAttribute('aria-hidden', 'true');
        if (tile.src) {
          const image = document.createElement('img');
          image.src = tile.src;
          if (tile.srcset) image.srcset = tile.srcset;
          image.sizes = `${size}px`;
          image.alt = '';
          image.width = size;
          image.height = size;
          media.append(image);
        }

        const caption = document.createElement('span');
        caption.className = 'clh__chip-label';
        caption.textContent = tile.label;

        link.append(media, caption);
        item.append(link);
        this.list.append(item);
      });

      this.reveal();
    }

    async refresh(cached) {
      const html = await this.fetchLatest();
      if (!html) {
        this.reveal();
        return;
      }
      const fresh = JSON.stringify(readTiles(new DOMParser().parseFromString(html, 'text/html')));
      store(dataKey(this.source), fresh);
      if (fresh !== cached) this.draw(fresh);
    }

    async fetchLatest() {
      const knownId = recall(idKey(this.source));
      if (knownId) {
        const html = await this.fetchSection(knownId);
        if (html) return html;
      }
      const found = await this.readHomepage();
      if (!found) return null;
      store(idKey(this.source), found.id);
      return found.html;
    }

    async fetchSection(id) {
      try {
        const url = `${this.root}${this.root.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(id)}`;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return null;
        return await response.text();
      } catch (e) {
        return null;
      }
    }

    async readHomepage() {
      try {
        const response = await fetch(this.root, { credentials: 'same-origin' });
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.querySelector(`[id^="shopify-section-"][id$="__${CSS.escape(this.source)}"]`);
        if (!section) return null;
        return { id: section.id.replace(/^shopify-section-/, ''), html: section.outerHTML };
      } catch (e) {
        return null;
      }
    }
  }

  customElements.define('tyoh-occ-publish', TyohOccPublish);
  customElements.define('tyoh-occ-chips', TyohOccChips);
})();
